<template>
    <div class="grid">
        <div class="grid__col-12">
            <div class="grid other-services-drawer ls-margin--bottom">
                <div
                    v-if="lawnTreatment && !featureFlags.flags.new_ltrs_flow_signup"
                    class="grid other-services-drawer__section ls-border-bottom-radius-none"
                    testid="lawntreatment-section"
                >
                    <div class="grid__col-md-6">
                        <p
                            class="other-services-drawer__section-heading ls-uppercase"
                            data-font-size="-1"
                        >
                            Add Lawn Treatment Package
                        </p>
                        <p class="other-services-drawer__section-description" data-font-size="-1">
                            Get the greenest lawn in {{ order.address.zip }} with our
                            <span class="ls-nowrap">8-treatment</span>
                            fertilization & weed control package.
                            <a
                                v-ls-track.click="{
                                    target: 'learn-more-about-lawn-treatment--link',
                                }"
                                class="ls-color--blue ls-nowrap"
                                testid="lawntreatment-learn-more"
                                @click.prevent="openModal('lawnTreatment')"
                            >
                                Learn More
                                <i class="fa fa-fw fa-question-circle"></i>
                            </a>
                        </p>
                    </div>

                    <div
                        class="grid__col-md-6 other-services-drawer__checkbox-col checkbox-padding-left-none"
                    >
                        <div class="other-services-drawer__checkbox">
                            <label class="ls-checkbox ls-height--100">
                                <input
                                    v-ls-track.change="{
                                        target: 'other-service-lawntreatment-checkbox',
                                    }"
                                    type="checkbox"
                                    :checked="lawnTreatmentIsInCart"
                                    @click.prevent="toggleLawnTreatment()"
                                    @keydown.enter="toggleLawnTreatment()"
                                />
                                <branded-checkbox
                                    :is-active="lawnTreatmentIsInCart"
                                    :is-plus-square="true"
                                    :is-big-icon="true"
                                />
                                <span>
                                    <span class="ls-bold" data-font-size="-1"
                                        >Yes, add a Treatment Package</span
                                    >
                                    <br />
                                    <span class="secondary-text ls-italic" data-font-size="-1"
                                        >8 treatments @ ${{ lawnTreatment.price.price }} each</span
                                    >
                                </span>
                            </label>
                        </div>
                    </div>
                </div>

                <div
                    class="grid other-services-drawer__section"
                    :class="{ 'ls-border-top-radius-none': lawnTreatment }"
                    testid="lawntreatment-section-two"
                >
                    <div class="grid__col-md-6">
                        <p
                            class="other-services-drawer__section-heading ls-uppercase"
                            data-font-size="-1"
                        >
                            Get quotes for other services
                        </p>
                        <p class="other-services-drawer__section-description" data-font-size="-1">
                            Need something else? We'll get you quotes after your first mow. You'll
                            have the chance to provide details on the next step.
                            <br />
                            <a
                                v-ls-track.click="{
                                    target: 'see-how-quoting-works--link',
                                }"
                                class="ls-color--blue"
                                @click.prevent="openModal('howQuotingWorks')"
                            >
                                See how quoting
                                <span class="ls-nowrap">
                                    works
                                    <i class="fa fa-fw fa-question-circle"></i>
                                </span>
                            </a>
                        </p>
                    </div>

                    <div class="grid__col-md-6 checkbox-padding-left-none">
                        <div>
                            <!-- necessary for proper rendering on android 4.x 'browser', block element required for proper wrapping -->
                            <div class="other-services-drawer__checkbox-col">
                                <div
                                    v-for="quote in quotes"
                                    :key="quote.service_id"
                                    class="other-services-drawer__checkbox quote-checkbox"
                                >
                                    <label class="ls-checkbox">
                                        <input
                                            :key="quote.service_id"
                                            type="checkbox"
                                            :checked="isChecked(quote.service_id)"
                                            @click.prevent="toggleQuote(quote)"
                                            @keydown.enter="toggleQuote(quote)"
                                        />
                                        <branded-checkbox
                                            :is-active="Boolean(isChecked(quote.service_id))"
                                            :is-plus-square="true"
                                            :is-big-icon="true"
                                        />
                                        <span class="ls-bold" data-font-size="-1">{{
                                            quote.name
                                        }}</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
/* eslint-disable camelcase */
import { mapState, mapGetters, mapActions } from 'vuex';
import * as types from '@/store/types';
import lsTrackService from '@/services/ls-track/ls-track.service';
import _ from 'lodash';
import { serviceIdToInstantQuote } from '@/constants';
import brandedCheckbox from '@/components/original-mydot/branded-checkbox/BrandedCheckbox.vue';

export default {
    components: {
        brandedCheckbox,
    },
    computed: {
        ...mapState(['cart', 'featureFlags', 'otherServices', 'order']),
        ...mapGetters({
            lawnTreatment: types.ORDER__GET_PRICED_LAWNTREATMENT,
            lawnTreatmentIsInCart: types.CART__GET_PRICED_LAWNTREATMENT_IS_IN_CART,
            quotesInCartByServiceId: types.OTHER_SERVICES__GET_QUOTES_IN_CART_BY_SERVICE_ID,
        }),
        quotes() {
            return _.filter(this.otherServices.selectableQuotes, (quote) =>
                process.env.VUE_APP_BRAND === 'lawnstarter' ? true : quote.service_id !== 9
            );
        },
    },

    methods: {
        ...mapActions({
            openModal: types.MODAL__OPEN_MODAL,
            toggleSelectableQuote: types.OTHER_SERVICES__TOGGLE_SELECTABLE_QUOTE,
            addServiceToCart: types.CART__ADD_SERVICE_TO_CART,
            removeServiceFromCart: types.CART__REMOVE_SERVICE_FROM_CART,
        }),

        isChecked(service_id) {
            if (service_id === 8 || service_id === 10) {
                const { instant_quotes } = _.get(this, 'cart');
                return _.find(instant_quotes, {
                    type: serviceIdToInstantQuote[service_id].type,
                });
            }

            return _.get(this.quotesInCartByServiceId, `${service_id}`);
        },
        toggleLawnTreatment() {
            if (this.lawnTreatmentIsInCart) {
                this.removeServiceFromCart(this.lawnTreatment);
            } else {
                this.addServiceToCart(this.lawnTreatment);
            }
        },
        toggleQuote(quote) {
            this.toggleSelectableQuote(quote);

            lsTrackService.track({
                type: 'change',
                target: 'other-service-checkbox',
                data: {
                    name: quote.name,
                    checked: Boolean(this.isChecked(quote.service_id)),
                },
            });
        },
    },
};
</script>

<style lang="scss" scoped>
$customDrawerPadding: 0.3125rem; // delta between rem(-3) and rem(0)

.other-services-drawer__section {
    border: 1px solid palette(gray, x-light);
    border-radius: 3px;
    padding: rem(0) 0;
}

.ls-border-top-radius-none {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border-top: 0;
}

.ls-border-bottom-radius-none {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
}

.other-services-drawer__section-heading {
    padding-bottom: $customDrawerPadding;
    font-weight: 700;
    color: palette(green, color-dodge);
}

.other-services-drawer__section-description {
    margin-top: 0;

    @include max-screen(breakpoint(sm, max)) {
        margin-bottom: rem(-3);
    }
}

.other-services-drawer__checkbox-col {
    display: flex;
    flex-wrap: wrap;

    @include max-screen(breakpoint(xs, max)) {
        padding-top: rem(-3);
    }

    .other-services-drawer__checkbox {
        flex: 1 1 50%;
        min-width: 50%;
        min-height: rem(4);

        &.quote-checkbox {
            max-width: 49%;
        }
    }
}

.checkbox-padding-left-none {
    @include min-screen(breakpoint(md)) {
        padding-left: 0 !important;
    }
}

.ls-checkbox {
    display: flex;
    margin-left: 0;
    margin-bottom: rem(-3);
    padding-right: rem(-3);
    user-select: none;

    span:before {
        // override spacing used elsewhere since we're using fa checkboxes
        content: '';
    }

    input {
        display: none;
    }
}

.themed-app {
    .other-services-drawer__section {
        border: 1px solid $theme-color-gray;
    }
    .other-services-drawer__section-heading {
        color: $theme-color-primary;
    }
}
</style>
<style lang="scss">
.other-services-drawer__section {
    .ls-checkbox i {
        padding-right: rem(0);
        margin-top: 3px;
        font-size: rem(0);
    }
}

.themed-lawnlove {
    .other-services-drawer__checkbox-col {
        .ls-bold {
            font-weight: 400;
        }
    }

    .other-services-drawer__section-heading {
        text-transform: capitalize;
        font-weight: 500;
    }

    .other-services-drawer__section {
        border-radius: 10px;
        .ls-checkbox .fa-plus-square {
            padding-right: 3px;
        }
        .ls-checkbox .fa-check {
            margin-top: 0;
            margin-right: 7px;
            font-size: 15px;
            padding: 0.5px;
        }
        & ~ .other-services-drawer__section {
            border-top: 0;
        }
    }
}
</style>
